import { type ReactNode } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { store, persistor } from '@doccareplus/kernel/application';
import { ThemeProvider } from '@doccareplus/shared/lib/theme';
import { ErrorBoundary, GlobalLoader, ServerWakeGate } from '@doccareplus/ui';
import { PortalAuthSessionBridge } from './PortalAuthSessionBridge';

// TanStack Query client — same read/mutation defaults as the clinic app.
const queryClient = new QueryClient({
  defaultOptions: {
    // The API client owns retries for explicitly transient statuses. Avoid a
    // second retry layer here, especially for server-side 500 responses.
    queries: { staleTime: 1000 * 60 * 5, gcTime: 1000 * 60 * 30, retry: false, refetchOnWindowFocus: false },
    mutations: { retry: 0 },
  },
});

// Provider order mirrors the clinic app: Redux → PersistGate → Query → Router → Theme.
// The portal has no clinic context, so the session bridge wires tokens + the X-Portal-Link-Id header only.
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <Provider store={store}>
      <PersistGate loading={<GlobalLoader />} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ThemeProvider defaultTheme="light" storageKey="portal-theme">
              <PortalAuthSessionBridge />
              <ServerWakeGate />
              <ErrorBoundary>{children}</ErrorBoundary>
            </ThemeProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  );
}
